import { Outlet } from 'react-router-dom';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { useNetworkStatus } from '@/modules/terminal/presentation/hooks/useNetworkStatus';
import { NetworkStatusBadge } from '@/modules/terminal/presentation/components/NetworkStatusBadge';
import { CurrentProfileBadge } from '@/modules/terminal/presentation/components/CurrentProfileBadge';
import { LocationStatusBadge } from '@/modules/terminal/presentation/components/LocationStatusBadge';
import { useGeolocation } from '@/shared/hooks/useGeolocation';

/**
 * KioskShell — layout persistente para TODAS las pantallas autenticadas del
 * kiosko (post-login, con perfil seleccionado).
 *
 * Contiene el header canónico del mockup:
 *   [Logo MyIntelli Clock]  ····· [🟢 En línea]  [🏢 QA1 VEN]
 *
 * Antes de este componente, el nav solo se renderizaba dentro de `HomePage`,
 * y todas las demás pantallas (Scanning, Success, Error, Portal, Admin, …)
 * aparecían sin header — colgadas del centro de la ventana. Extraerlo al
 * shell garantiza que cualquier ruta hija herede el mismo header sin duplicar
 * código (HU-A del audit 2026-07-14).
 *
 * NOTA: NO se usa aquí en `RequireAuth` sino en un nivel más adentro,
 * después de `RequireProfile`, porque el badge de perfil necesita saber qué
 * perfil está seleccionado. `/login` y `/select-profile` NO usan este shell.
 */
export function KioskShell() {
  const { T, C } = useTheme();
  const { selectedRole } = useProfile();
  const networkStatus = useNetworkStatus();
  const { coordinates, loading: locationLoading, retry: retryLocation } = useGeolocation();

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background: T.bg,
        display: 'flex',
        flexDirection: 'column',
        transition: 'background .3s',
      }}
    >
      {/* NAV — canonical del mockup: 55 px de alto, logo left, badges right. */}
      <nav
        style={{
          background: T.navBg,
          borderBottom: `1px solid ${T.navBorder}`,
          padding: '0 34px',
          height: 55,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          boxShadow: '0 1px 8px rgba(0,0,0,.05)',
        }}
      >
        <img
          src="/logo-myintelli-clock-full.png"
          alt="myIntelli Clock"
          style={{ height: 36, objectFit: 'contain' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <LocationStatusBadge coordinates={coordinates} loading={locationLoading} onRetry={retryLocation} T={T} C={C} />
          <NetworkStatusBadge status={networkStatus} T={T} C={C} />
          <CurrentProfileBadge
            profileName={selectedRole?.name ?? '—'}
            C={C}
          />
        </div>
      </nav>

      {/* Content area — cada ruta hija se pinta como `<main>` con flex:1 y
          se centra vertical/horizontalmente por su cuenta. */}
      <Outlet />
    </div>
  );
}
